import React from 'react';
import {
  shape, arrayOf, func, string,
} from 'prop-types';
import {
  Typography, DownloadIcon, DashboardDetails,
  PhoneNumber, EmailLink, Link, COLORS,
} from '@hyphengroup/cag-components';
import {
  withTracking,
  loginDashboardPolicyWordingEvent,
  loginDashboardClaimEmailEvent,
  loginDashboardCallHotlineEvent,
  loginDashboardClaimFormEvent,
  loginDashboardProductDetailsClickEvent,
} from 'components/tracking';
import { getObjectFromArray } from 'pages/Verticals/ResultsPage/ResultsPageUtils';
import { path } from 'ramda';
import moment from 'moment';
import NumberFormat from 'react-number-format';
import { formatBytes, getDashboardDesktopMenuBar } from 'helpers/Utils';

const mapStatusToColor = {
  ACTIVE: COLORS.GREEN,
  UPCOMING: COLORS.BKGROUND_04,
  EXPIRED: COLORS.ALERT_02,
  CANCELLED: COLORS.BKGROUND_04,
};

export const mapPolicyStatus = {
  ACTIVE: 'ACTIVE',
  UPCOMING: 'UPCOMING',
  CANCELLED: 'CANCELLED',
  EXPIRED: 'EXPIRED',
  FREELOOK: 'CANCELLED',
};

function DashboardMoreInfoPA({
  data, header: { logo }, footer, history, baseURL, showAccountMenu, track,
}) {
  const customerProducts = data.customerProfile?.customerProducts?.products;
  const product = customerProducts && customerProducts.length && customerProducts[0];
  if (!product) {
    return null;
  }

  const {
    policyNumber, policyStatus, enrollmentDate, premium,
    policyStartDate, policyEndDate, purchasedBy, insured,
    product: {
      productName, coverage, policyDocumentation, provider,
      claims: { phone: claimsPhone, email: claimsEmail, link: claimsLink },
    },
  } = product;
  const staticText = getObjectFromArray(path(['pageConfig', 'staticPageText', 'staticText'], data));
  const accidentalDeath = coverage.detail.find(item => item && item.type?.key === 'ACCIDENTAL_DEATH')?.valueString;
  const medicalExpenses = coverage.detail.find(item => item && item.type?.key === 'MEDICAL_HOSPITAL_LOCAL')?.valueString;
  const dailyHospitalIncome = coverage.detail.find(item => item && item.type?.key === 'HOSPITAL_CASH_INCOME')?.valueString;
  const currencyPrefix = staticText.DOLLAR_PREFIX;
  const policyWordingFile = policyDocumentation.find(document => document && document.type.key === 'POLICY_WORDINGS')?.file;
  const fileSizes = [
    staticText.FILE_SIZE_BYTES,
    staticText.FILE_SIZE_KB,
    staticText.FILE_SIZE_MB,
    staticText.FILE_SIZE_GB,
  ];

  function getFormatDate(date) {
    return moment(date).format('DD MMM YYYY');
  }

  const paDetailsData = [
    {
      sections: [
        {
          header: staticText.PURCHASE_DETAILS_PA_TITLE_CLAIM_DETAILS,
          rows: [
            {
              title: staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_CLAIMS_EMAIL,
              content: (
                <EmailLink
                  variant="bold"
                  email={claimsEmail}
                  onClick={() => loginDashboardClaimEmailEvent(track, 'PA', product)}
                >
                  {claimsEmail}
                </EmailLink>
              ),
              contentMobile: (
                <EmailLink
                  variant="bold"
                  email={claimsEmail}
                  onClick={() => loginDashboardClaimEmailEvent(track, 'PA', product)}
                >
                  {staticText.PURCHASE_DETAILS_PA_FIELD_EMAIL}
                </EmailLink>
              ),
            },
            {
              title: staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_CLAIMS_HOTLINE,
              content: (
                <PhoneNumber
                  number={claimsPhone}
                  showIcon={false}
                  variant="bold"
                  onClick={() => loginDashboardCallHotlineEvent(track, 'PA', product)}
                />
              ),
            },
            {
              title: staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_CLAIMS_FORM,
              content: (
                <Link
                  to={claimsLink.href}
                  underline
                  variant="darkBlue"
                  padding="0"
                  onClick={() => loginDashboardClaimFormEvent(track, 'PA', product)}
                >
                  <Typography variant="secondary" weight="bold">
                    {staticText.PURCHASE_DETAILS_PA_FIELD_SUBMIT_HERE}
                  </Typography>
                </Link>
              ),
            },
          ],
        },
        {
          header: staticText.PURCHASE_DETAILS_PA_TITLE_POLICY_INFORMATION,
          rows: [
            {
              title: staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_POLICY_NUMBER,
              content: policyNumber,
            },
            {
              title: staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_POLICY_END_DATE,
              content: getFormatDate(policyEndDate),
            },
            {
              title: staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_POLICY_START_DATE,
              content: getFormatDate(policyStartDate),
            },
            {
              title: staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_POLICY_STATUS,
              content: (
                <Typography
                  variant="secondary"
                  sizeFont="px14"
                  weight="bold"
                  color={mapStatusToColor[policyStatus]}
                >
                  {staticText[`PURCHASE_PA_STATUS_${mapPolicyStatus[policyStatus]}`]}
                </Typography>
              ),
            },
            {
              title: staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_APPLICANT_NAME,
              content: purchasedBy.fullName,
            },
            {
              title: staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_NUMBER_OF_INSURED_PERSONS,
              content: insured.length,
            },
            {
              title: staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_DATE_OF_ENROLMENT,
              content: getFormatDate(enrollmentDate),
            },
            {
              title: staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_PREMIUM_PAID,
              content: <NumberFormat thousandSeparator={true} displayType="text" prefix={currencyPrefix} value={premium} />,
            },
          ],
        },
        {
          header: staticText.PURCHASE_DETAILS_PA_TITLE_INSURED_PERSON,
          rows: insured.map((insuredPerson, index) => ({
            title: `${staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_INSURED_NAME} ${index + 1}`,
            content: insuredPerson.fullName,
          })),
        },
        {
          header: staticText.PURCHASE_DETAILS_PA_TITLE_COVERAGE,
          rows: [
            {
              title: staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_ACCIDENTAL_DEATH,
              content: <NumberFormat thousandSeparator={true} displayType="text" prefix={currencyPrefix} value={accidentalDeath} />,
            },
            {
              title: staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_MEDICAL_EXPENSES,
              content: <NumberFormat thousandSeparator={true} displayType="text" prefix={currencyPrefix} value={medicalExpenses} />,
            },
            {
              title: staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_DAILY_HOSPITAL_INCOME,
              content: <NumberFormat thousandSeparator={true} displayType="text" prefix={currencyPrefix} value={dailyHospitalIncome} />,
            },
          ],
        },
        {
          header: staticText.PURCHASE_DETAILS_PA_TITLE_POLICY_MATERIALS,
          rows: [
            {
              title: staticText.PURCHASE_DETAILS_PA_FIELD_LABEL_POLICY_WORDINGS,
              content: (
                <DownloadIcon
                  link={policyWordingFile.url}
                  label={formatBytes(policyWordingFile.fileSize, fileSizes)}
                  onClick={() => loginDashboardPolicyWordingEvent(track, 'PA', product)}
                />
              ),
              alignItems: 'center',
            },
          ],
        },
      ],
    },
  ];

  return (
    <DashboardDetails
      data={paDetailsData}
      productTitle={productName}
      productLogo={provider.providerImage.url}
      productDetailsButton={policyStatus === 'ACTIVE' && {
        name: staticText.PURCHASE_DETAILS_PA_CTA_MAKE_CLAIM,
        onClick: () => {
          loginDashboardProductDetailsClickEvent(track, 'PA', product);
          const win = window.open(`/external?url=${claimsLink.href}`, '_blank');
          win.focus();
        },
      }}
      productLogoStyle="outline"
      onBackClick={() => history.push(`${baseURL}/${staticText.DASHBOARD_ROUTE_NAME}`)}
      backBtnLabel={staticText.BACK}
      desktopMenuBar={getDashboardDesktopMenuBar(staticText)}
      siteLogo={{
        link: logo?.link?.href,
        imageTitle: logo?.title,
        imageUrl: footer?.logo?.image?.url,
      }}
      onRightIconClick={showAccountMenu}
    />
  );
}

DashboardMoreInfoPA.defaultProps = {
  track: null,
};
DashboardMoreInfoPA.propTypes = {
  track: func,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  baseURL: string.isRequired,
  data: shape({
    customerProfile: shape({
      customerProducts: shape({
        products: arrayOf(
          shape({
            policyNumber: string.isRequired,
            policyStatus: string.isRequired,
            customer: shape({
              firstName: string.isRequired,
              familyName: string.isRequired,
            }).isRequired,
            enrollmentDate: string,
            premium: string.isRequired,
            product: shape({
              productName: string.isRequired,
              coverage: shape({
                coverage: arrayOf(shape({
                  coverageType: String,
                  value: String,
                })),
              }),
              policyDocumentation: arrayOf(shape({
                type: String,
                file: shape({
                  url: String,
                  fileSize: String,
                }),
              })),
            }),
            provider: shape({
              providerImage: shape({
                url: String,
              }),
            }),
            ctaButton: String,
          }).isRequired
        ).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  footer: shape({
    logo: shape({
      image: shape({
        url: string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  header: shape({
    logo: shape({
      link: shape({
        href: string.isRequired,
      }).isRequired,
      title: string.isRequired,
    }).isRequired,
  }).isRequired,
  showAccountMenu: func.isRequired,
};

export default withTracking(DashboardMoreInfoPA);
